import React, { useEffect, useState, useCallback, memo, useMemo, useRef } from 'react';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import Skeleton from '@mui/material/Skeleton';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ButtonGroup from '@mui/material/ButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import { apiUrl, fetchJson } from '../apiBase';

const AttendanceForm = ({ onSuccess, onError, schoolId }) => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]); // filtered by class
  const studentsRequestId = useRef(0);
  const [studentNotes, setStudentNotes] = useState({}); // { [studentId]: note }

  const normalizeClassCode = useCallback((value) => {
    return String(value ?? '')
      .replace(/\u00A0/g, ' ') // NBSP -> space
      .trim()
      .toLocaleUpperCase('tr-TR');
  }, []);

  const todayTR = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const [date, setDate] = useState(todayTR);

  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [savingById, setSavingById] = useState({}); // { [id]: true }

  // Helpers (stable across renders so effects don't re-run due to parent re-renders)
  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);

  const notifyError = useCallback((msg) => { onErrorRef.current && onErrorRef.current(msg); }, []);
  const notifySuccess = useCallback((msg) => { onSuccessRef.current && onSuccessRef.current(msg); }, []);

  // Load classes
  useEffect(() => {
    const run = async () => {
      if (!schoolId) return;
      setLoadingClasses(true);
      setClasses([]);
      setSelectedClass(null);
      try {
        const res = await fetchJson(apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}`));
        if (!res.ok) throw new Error('classes');
        const data = await res.json();
        const items = (data || [])
          .map(c => ({ id: c?.id, name: normalizeClassCode(c?.name) }))
          .filter(c => c.id != null && c.name);
        items.sort((a, b) => String(a.name).localeCompare(String(b.name), 'tr-TR'));
        setClasses(items);
        setSelectedClass(items[0] || null);
      } catch (e) {
        notifyError('Şubeler yüklenemedi');
      } finally { setLoadingClasses(false); }
    };
    run();
  }, [schoolId, notifyError, normalizeClassCode]);

  // Load only the selected class roster for this date
  const loadStudents = useCallback(async () => {
    const requestId = ++studentsRequestId.current;
    setStudents([]);
    setStudentNotes({});
    if (!schoolId || !date || !selectedClass?.id) { setLoadingStudents(false); return; }
    setLoadingStudents(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/students-attendance/${encodeURIComponent(date)}/school/${encodeURIComponent(schoolId)}?class_id=${encodeURIComponent(selectedClass.id)}`));
      if (!res.ok) throw new Error('students');
      const all = await res.json();
      if (requestId !== studentsRequestId.current) return;
      const filtered = (all || []).sort((a, b) => (
        `${a?.name || ''} ${a?.surname || ''}`.localeCompare(
          `${b?.name || ''} ${b?.surname || ''}`,
          'tr-TR',
          { sensitivity: 'base' },
        ) || Number(a?.id || 0) - Number(b?.id || 0)
      ));
      setStudents(filtered);
      const notes = {};
      filtered.forEach(s => { notes[String(s.id)] = String(s.notes || ''); });
      setStudentNotes(notes);
    } catch (e) {
      if (requestId !== studentsRequestId.current) return;
      notifyError('Öğrenciler yüklenemedi');
      setStudents([]);
      setStudentNotes({});
    } finally { if (requestId === studentsRequestId.current) setLoadingStudents(false); }
  }, [schoolId, date, selectedClass, notifyError]);

  useEffect(() => {
    loadStudents();
    return () => { studentsRequestId.current += 1; };
  }, [loadStudents]);

  // Compute the maximum length of visible student_no (fallback to id)
  const maxNoLen = useMemo(() => {
    const arr = students || [];
    return arr.reduce((m, s) => {
      const no = String((s?.student_no ?? s?.id ?? ''));
      return Math.max(m, no.length);
    }, 0);
  }, [students]);

  const handleStudentNoteChange = (studentId, value) => {
    setStudentNotes(prev => ({ ...prev, [String(studentId)]: String(value).slice(0, 500) }));
  };

  const updateLocalStatus = (studentId, status) => {
    setStudents(prev => prev.map(s => String(s.id) === String(studentId) ? { ...s, status } : s));
  };

  const callUpsertSingle = async (studentId, status) => {
    if (!schoolId || !date) return;
    const sid = String(studentId);
    const prev = (students.find(s => String(s.id) === sid) || {}).status;
    const isClear = status == null;
    updateLocalStatus(sid, isClear ? 'not_recorded' : status);
    setSavingById(prevMap => ({ ...prevMap, [sid]: true }));
    try {
      const res = isClear
        ? await fetchJson(apiUrl(`/admin/attendance/${encodeURIComponent(schoolId)}/student`), {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ student_id: sid, date: String(date) }),
          })
        : await fetchJson(apiUrl(`/admin/attendance/${encodeURIComponent(schoolId)}/student`), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              student_id: sid,
              date: String(date),
              status: String(status),
              notes: (studentNotes[sid] || '').slice(0, 500),
            }),
          });
      if (!res.ok) throw new Error('save');
      notifySuccess(isClear ? 'Seçim kaldırıldı' : 'Kaydedildi');
    } catch (e) {
      // Revert on failure
      updateLocalStatus(sid, prev);
      notifyError('Kaydetme başarısız');
    } finally {
      setSavingById(prevMap => ({ ...prevMap, [sid]: false }));
    }
  };

  const bulkMarkUnset = async (status) => {
    if (!schoolId || !date || !selectedClass) return;
    setBulkSaving(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/attendance/${encodeURIComponent(schoolId)}/bulk-mark-unset`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: String(date), status: String(status), class_name: normalizeClassCode(selectedClass.name) }),
      });
      if (!res.ok) throw new Error('bulk');
      // Optimistically update local: only rows with not_recorded
      setStudents(prev => prev.map(s => (String(s.status || '') === 'not_recorded') ? { ...s, status } : s));
      notifySuccess('Kayıtsızlar güncellendi');
    } catch (e) {
      notifyError('Toplu işaretleme başarısız');
    } finally { setBulkSaving(false); }
  };

  return (
    <>
      {/* Filters */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={6}>
            <Autocomplete
              size="small"
              options={classes}
              value={selectedClass || null}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              onChange={(_, v) => setSelectedClass(v || null)}
              loading={loadingClasses}
              renderInput={(params) => <TextField {...params} label="Şube" placeholder="Seçin" fullWidth />}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              size="small"
              type="date"
              label="Tarih"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Student statuses panel */}
      {selectedClass && (
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>Öğrenci Durumları</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" color="success" disabled={loadingStudents || bulkSaving} onClick={() => bulkMarkUnset('present')}>
                Kayıtsızları Mevcut Yap
              </Button>
            </Box>
          </Box>
          {loadingStudents ? (
            <List>
              {Array.from({ length: 6 }).map((_, i) => (
                <ListItem key={`sk-${i}`} divider>
                  <Skeleton variant="text" width="60%" />
                </ListItem>
              ))}
            </List>
          ) : students.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
              <Typography>Bu şube için öğrenci bulunamadı.</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {students.map((student) => (
                <StudentRow
                  key={student.id}
                  student={student}
                  note={studentNotes[student.id] || ''}
                  onNoteChange={handleStudentNoteChange}
                  onSetStatus={callUpsertSingle}
                  disabled={!!savingById[String(student.id)]}
                  maxNoLen={maxNoLen}
                />
              ))}
            </List>
          )}
        </Paper>
      )}
    </>
  );
};

// Memoized row to avoid re-rendering all rows on a single update
const StudentRow = memo(function StudentRow({ student, note, onNoteChange, onSetStatus, disabled, maxNoLen }) {
  const current = String(student.status || '');
  return (
  <ListItem divider disableGutters sx={{ px: { xs: .5, sm: .5 }, py: .9 }}>
      <Box sx={{
        width: '100%',
        display: { xs: 'flex', sm: 'grid' },
        alignItems: 'center',
        gap: { xs: .9, sm: 1 },
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        overflow: 'hidden',
        gridTemplateColumns: { sm: '240px 210px 1fr' }, // name | buttons | note grows
      }}>
        <Box sx={{
          gridColumn: '1 / 2',
          flex: { xs: '1 1 100%', sm: '0 0 240px' },
          width: { sm: '240px' },
          minWidth: 0,
        }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '.95rem', sm: '1rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: { xs: 'nowrap', sm: 'nowrap' },
              display: { xs: 'block', sm: 'block' },
              WebkitLineClamp: { xs: 'unset', sm: 'unset' },
              WebkitBoxOrient: 'unset',
            }}
          >
            {(() => {
              const no = String(student.student_no || student.id || '');
              const widthCh = Math.max(maxNoLen || 0, no.length);
              const block = no.padEnd(widthCh, ' ');
              return (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 1, whiteSpace: 'nowrap' }}>
                  <Box
                    component="span"
                    sx={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      display: 'inline-block',
                      width: `${widthCh}ch`,
                      whiteSpace: 'pre',
                    }}
                  >
                    {block}
                  </Box>
                  <Box component="span">{student.name} {student.surname}</Box>
                </Box>
              );
            })()}
          </Typography>
        </Box>

        <ButtonGroup
          size="small"
          aria-label="durum"
          sx={{
            borderRadius: '999px',
            overflow: 'visible',
            boxShadow: 'none',
            border: '1px solid',
            borderColor: (theme) => theme.palette.divider,
            gridColumn: { sm: '2 / 3' },
            justifySelf: { sm: 'start' },
            flex: { xs: '0 0 auto', sm: 'initial' },
            whiteSpace: 'nowrap',
            '& .MuiButtonGroup-grouped': {
              textTransform: 'none',
              fontWeight: 700,
              px: { xs: .55, sm: .7 },
              py: .4,
              minWidth: { xs: 56, sm: 60 },
              borderColor: (theme) => theme.palette.divider,
            },
            '& .MuiButtonGroup-grouped:not(:last-of-type)': { borderRightColor: 'transparent' },
            '& .MuiButtonGroup-grouped:first-of-type': { borderTopLeftRadius: 999, borderBottomLeftRadius: 999 },
            '& .MuiButtonGroup-grouped:last-of-type': { borderTopRightRadius: 999, borderBottomRightRadius: 999 },
          }}
        >
          <Tooltip title="Devamsız" placement="top">
            <Button
              color="error"
              variant={current === 'absent' ? 'contained' : 'outlined'}
              onClick={() => onSetStatus(student.id, current === 'absent' ? null : 'absent')}
              aria-label="Devamsız"
              disabled={disabled}
            >
              Yok
            </Button>
          </Tooltip>
          <Tooltip title="Geç Geldi" placement="top">
            <Button
              color="warning"
              variant={current === 'late' ? 'contained' : 'outlined'}
              onClick={() => onSetStatus(student.id, current === 'late' ? null : 'late')}
              aria-label="Geç Geldi"
              disabled={disabled}
            >
              Geç
            </Button>
          </Tooltip>
          <Tooltip title="Mevcut" placement="top">
            <Button
              color="success"
              variant={current === 'present' ? 'contained' : 'outlined'}
              onClick={() => onSetStatus(student.id, current === 'present' ? null : 'present')}
              aria-label="Mevcut"
              disabled={disabled}
            >
              Tam
            </Button>
          </Tooltip>
        </ButtonGroup>

        <Box sx={{
          gridColumn: { sm: '3 / 4' },
          flex: { xs: '1 1 0', sm: '1 1 auto' },
          width: { xs: 'auto', sm: 'auto' },
          minWidth: { xs: 0, sm: 160 },
          ml: { xs: 0, sm: 0 },
          mt: { xs: 0, sm: 0 },
          display: 'block',
        }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Not ekle..."
            value={note}
            onChange={(e) => onNoteChange(student.id, e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '0.8rem',
                '& fieldset': { borderColor: 'divider' },
                '&:hover fieldset': { borderColor: 'primary.main' },
              },
            }}
          />
        </Box>
      </Box>
    </ListItem>
  );
}, (prev, next) => {
  // Re-render only if relevant fields changed
  return (
    prev.student.id === next.student.id &&
    String(prev.student.student_no || '') === String(next.student.student_no || '') &&
    prev.student.name === next.student.name &&
    prev.student.surname === next.student.surname &&
    String(prev.student.status || '') === String(next.student.status || '') &&
    String(prev.note || '') === String(next.note || '') &&
    !!prev.disabled === !!next.disabled &&
    Number(prev.maxNoLen || 0) === Number(next.maxNoLen || 0)
  );
});

export default AttendanceForm;
